declare global {
  interface Window {
    Razorpay: new (options: Record<string, unknown>) => {
      open: () => void;
      on: (event: string, handler: (response: unknown) => void) => void;
    };
  }
}

export type RazorpayCheckoutPayload = {
  checkoutMode: 'razorpay';
  subscriptionId: string;
  keyId: string;
  name: string;
  description: string;
  successUrl: string;
  cancelUrl: string;
  prefill?: { email?: string; name?: string };
};

export type RazorpayOrderPayload = {
  order_id: string;
  amount: number;
  currency: string;
  key_id?: string;
};

export type RazorpayPaymentSuccess = {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
};

export function isRazorpayCheckoutResult(data: unknown): data is RazorpayCheckoutPayload {
  return (
    !!data &&
    typeof data === 'object' &&
    (data as RazorpayCheckoutPayload).checkoutMode === 'razorpay'
  );
}

export function resolveRazorpayKeyId(order?: RazorpayOrderPayload): string {
  return (
    order?.key_id ||
    import.meta.env.VITE_RAZORPAY_KEY_ID ||
    ''
  );
}

function loadRazorpayScript(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Razorpay checkout'));
    document.body.appendChild(script);
  });
}

export async function openRazorpaySubscriptionCheckout(
  payload: RazorpayCheckoutPayload,
): Promise<void> {
  await loadRazorpayScript();
  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: payload.keyId,
      subscription_id: payload.subscriptionId,
      name: payload.name,
      description: payload.description,
      prefill: payload.prefill,
      theme: { color: '#4F46E5' },
      handler: () => {
        window.location.href = payload.successUrl;
        resolve();
      },
      modal: {
        ondismiss: () => reject(new Error('Checkout cancelled')),
      },
    });
    rzp.open();
  });
}

/** Razorpay Standard Web Checkout — order + payment signature verification. */
export async function openRazorpayStandardCheckout(params: {
  order: RazorpayOrderPayload;
  name?: string;
  description?: string;
  prefill?: { email?: string; name?: string };
  verifyPayment: (payload: RazorpayPaymentSuccess) => Promise<unknown>;
}): Promise<void> {
  await loadRazorpayScript();

  const keyId = resolveRazorpayKeyId(params.order);
  if (!keyId) {
    throw new Error('Razorpay key is not configured (VITE_RAZORPAY_KEY_ID or order.key_id)');
  }

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: keyId,
      amount: params.order.amount,
      currency: params.order.currency,
      order_id: params.order.order_id,
      name: params.name || 'CoTaskr',
      description: params.description || 'Payment',
      prefill: params.prefill,
      theme: { color: '#4F46E5' },
      handler: async (response: RazorpayPaymentSuccess) => {
        try {
          await params.verifyPayment(response);
          resolve();
        } catch (err) {
          reject(err);
        }
      },
      modal: {
        ondismiss: () => reject(new Error('Checkout cancelled')),
      },
    });

    rzp.on('payment.failed', (response: { error?: { description?: string } }) => {
      reject(new Error(response?.error?.description || 'Payment failed'));
    });

    rzp.open();
  });
}

export async function startBillingCheckout(result: unknown): Promise<void> {
  if (isRazorpayCheckoutResult(result)) {
    await openRazorpaySubscriptionCheckout(result);
    return;
  }
  const url = (result as { url?: string })?.url;
  if (url) {
    window.location.href = url;
    return;
  }
  throw new Error('Checkout did not return a valid session.');
}

export async function startRazorpayStandardPayment(params: {
  amount: number;
  currency?: string;
  receipt?: string;
  name?: string;
  description?: string;
  prefill?: { email?: string; name?: string };
  createOrder: (body: { amount: number; currency?: string; receipt?: string }) => Promise<RazorpayOrderPayload>;
  verifyPayment: (payload: RazorpayPaymentSuccess) => Promise<unknown>;
}): Promise<void> {
  const order = await params.createOrder({
    amount: params.amount,
    currency: params.currency,
    receipt: params.receipt,
  });
  await openRazorpayStandardCheckout({
    order,
    name: params.name,
    description: params.description,
    prefill: params.prefill,
    verifyPayment: params.verifyPayment,
  });
}
